import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { AdminGuard } from './components/admin-guard';
import { AuthGuard } from './components/auth-guard';
import { Layout } from './components/layout';
import { UpdateNotification } from './components/update-notification';
import { useAuthContext } from './contexts/auth.context';
import { isCountTask, isDayTask } from './dto/habit';
import { PartnerShipStatus } from './dto/partnership';
import { getTodaysRecord } from './dto/record';
import { User } from './dto/user';
import { useHabit } from './hooks/habit.hook';
import { usePartnership } from './hooks/partnership.hook';
import { useReading } from './hooks/reading.hook';
import { useRecord } from './hooks/record.hook';
import { AuthResetScreen } from './screens/auth-reset.screen';
import AuthScreen from './screens/auth.screen';
import ReleasesScreen from './screens/releases.screen';
import { DescriptionScreen } from './screens/description.screen';
import ErrorScreen from './screens/error.screen';
import HomeScreen from './screens/home.screen';
import { PrivacyScreen } from './screens/privacy.screen';
import HabitAddScreen from './screens/user/habit-add.screen';
import HabitBundleScreen from './screens/user/habit-bundle.screen';
import { HabitEditScreen } from './screens/user/habit-edit.screen';
import HabitNewScreen from './screens/user/habit-new.screen';
import HabitScreen from './screens/user/habit.screen';
import PartnerAddScreen from './screens/user/partner-add.screen';
import PartnerDetailsScreen from './screens/user/partner-details.screen';
import PartnerScreen from './screens/user/partner.screen';
import ReadingScreen from './screens/user/reading.screen';
import ReportScreen from './screens/user/report.screen';
import SettingScreen from './screens/user/setting.screen';
import StatisticScreen from './screens/user/statistic.screen';
import AppStatisticScreen from './screens/user/app-statistic.screen';
import { useAppStore } from './stores/app.store';
import { getThemeColor } from './util/util';

const router = createBrowserRouter([
  {
    element: (
      <Layout>
        <Outlet />
      </Layout>
    ),
    errorElement: <ErrorScreen />,
    children: [
      // generic
      {
        path: '/privacy',
        element: <PrivacyScreen />,
      },
      {
        path: '/description',
        element: <DescriptionScreen />,
      },
      {
        path: '/releases',
        element: <ReleasesScreen />,
      },
      {
        path: '/auth/reset',
        element: <AuthResetScreen />,
      },
      // home
      {
        element: <AuthGuard isProtected={false} />,
        children: [
          {
            path: '/',
            element: <HomeScreen />,
          },
          {
            path: '/auth',
            element: <AuthScreen />,
          },
        ],
      },
      // user
      {
        element: <AuthGuard isProtected={true} />,
        children: [
          {
            path: '/habits',
            element: <HabitScreen />,
          },
          {
            path: '/habits/add',
            element: <HabitAddScreen />,
          },
          {
            path: '/habits/add/:category',
            element: <HabitAddScreen />,
          },
          {
            path: '/habits/new',
            element: <HabitNewScreen />,
          },
          {
            path: '/habits/edit',
            element: <HabitEditScreen />,
          },
          {
            path: '/habits/bundle',
            element: <HabitBundleScreen />,
          },
          {
            path: '/statistic',
            element: <StatisticScreen />,
          },
          {
            path: '/statistic/report',
            element: <ReportScreen />,
          },
          {
            path: '/partners',
            element: <PartnerScreen />,
          },
          {
            path: '/partners/add',
            element: <PartnerAddScreen />,
          },
          {
            path: '/partners/details',
            element: <PartnerDetailsScreen />,
          },
          {
            path: '/readings',
            element: <ReadingScreen />,
          },
          {
            path: '/settings',
            element: <SettingScreen />,
          },
          {
            element: <AdminGuard />,
            children: [
              {
                path: '/app-stats',
                element: <AppStatisticScreen />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

function App() {
  const { user } = useAuthContext();
  const { loadHabits, resetHabits, habits } = useHabit();
  const { loadRecords, resetRecords, recordsFor } = useRecord();
  const { loadPartnerships, resetPartnerships, partnerships } = usePartnership();
  const { loadReadings, resetReadings, dailyReading, hasReadingToDo } = useReading();
  const { i18n } = useTranslation();

  // theme control
  const theme = useAppStore((s) => s.theme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', getThemeColor('--color-base-200'));
  }, [theme]);

  useEffect(() => {
    if (user) return loadData(user);
    resetData();
  }, [user]);

  // i18n
  useEffect(() => {
    onLanguageChanged(i18n.language);

    i18n.on('languageChanged', onLanguageChanged);

    return () => i18n.off('languageChanged', onLanguageChanged);
  }, [i18n]);

  function onLanguageChanged(language: string) {
    document.documentElement.lang = language;
  }

  // data loader
  function loadData(user: User): () => void {
    const unsubscribeHabits = loadHabits(user);
    const unsubscribeRecords = loadRecords(user);
    const unsubscribePartnerships = loadPartnerships(user);
    const unsubscribeReadings = loadReadings(user);

    return () => {
      unsubscribeHabits();
      unsubscribeRecords();
      unsubscribePartnerships();
      unsubscribeReadings();
    };
  }

  function resetData() {
    resetHabits();
    resetRecords();
    resetPartnerships();
    resetReadings();
  }

  // news bubbles
  const setNews = useAppStore((s) => s.setNews);

  useEffect(() => {
    const pendingHabits = habits.reduce((c, h) => {
      const records = recordsFor(h);
      if (!isCountTask(h) && isDayTask(h, new Date()) && !h.end && !getTodaysRecord(records)) c += 1;
      return c;
    }, 0);

    setNews('habits', pendingHabits);
  }, [setNews, habits, recordsFor]);

  useEffect(() => {
    const pendingPartnerRequests = partnerships.filter(
      (i) => !i.isMyRequest && i.status === PartnerShipStatus.PENDING,
    ).length;
    setNews('partners', pendingPartnerRequests);
  }, [setNews, partnerships]);

  useEffect(() => {
    setNews('readings', hasReadingToDo ? 1 : 0);
  }, [setNews, hasReadingToDo]);

  // dynamic menu control
  const setHiddenMenus = useAppStore((s) => s.setHiddenMenus);

  useEffect(() => {
    setHiddenMenus(dailyReading ? [] : ['readings']);
  }, [setHiddenMenus, dailyReading]);

  return (
    <>
      <UpdateNotification />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
